import { useEffect, useRef, useState } from "react";

interface AnimatedCounterProps {
  end: number | string;
  suffix?: string;
  prefix?: string;
  label: string;
  duration?: number;
}

const AnimatedCounter = ({ 
  end, 
  suffix = "", 
  prefix = "", 
  label,
  duration = 2000 
}: AnimatedCounterProps) => {
  const [count, setCount] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !isVisible) {
          setIsVisible(true);
        }
      },
      { threshold: 0.3 }
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => observer.disconnect();
  }, [isVisible]);

  useEffect(() => {
    if (!isVisible || typeof end !== "number") return;

    let startTime: number;
    const animate = (currentTime: number) => {
      if (!startTime) startTime = currentTime;
      const progress = Math.min((currentTime - startTime) / duration, 1);
      
      const easeOut = 1 - Math.pow(1 - progress, 3);
      setCount(Math.floor(easeOut * end));

      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    };

    requestAnimationFrame(animate);
  }, [isVisible, end, duration]);

  const displayValue = typeof end === "number" ? count : end;

  return (
    <div ref={ref} className="text-center">
      <div className="text-6xl md:text-7xl lg:text-8xl font-bold text-foreground mb-3">
        {prefix}{displayValue}{suffix}
      </div>
      <div className="text-lg md:text-xl font-light text-muted-foreground">{label}</div>
    </div>
  );
};

export default AnimatedCounter;
